import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";
import AlertModal from "../components/AlertModal";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { BASE_URL } from "../config";

const SignIn = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });
  const [deactivationMessage, setDeactivationMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectMessage = location.state?.message || "";

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Get user info from Google
        const { data } = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );

        // Send to backend for login/signup
        await axios.post(`${BASE_URL}/api/auth/google`, data, {
          withCredentials: true, // so cookies are set
        });

        window.location.href = "/dashboard"; // or use navigate
      } catch (err) {
        console.error("Google login error:", err);
      }
    },
    onError: (error) => console.log("Login Failed:", error),
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true); // Start loading

    try {
      await axios.post(
        `${BASE_URL}/api/users/login`,
        {
          email: form.email,
          password: form.password,
        },
        { withCredentials: true }
      );

      await login(); // fetch profile and set user in context

      setSuccess("Login successful. Redirecting...");
      setTimeout(() => {
        const redirectPath = location.state?.from || "/dashboard";
        navigate(redirectPath, { replace: true });
      }, 1200);
    } catch (err) {
      const msg = err.response?.data?.message || "Login failed. Please try again.";

      // Special handling for Google accounts
      if (msg.includes("Google")) {
        setError("This account was created with Google. Please use the Google sign-in button.");
      } else {
        setError(msg);
      }

      setLoading(false); // 🔹 Stop loading immediately on failure
    }
  };

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
        setLoading(false); // 🔹 Ensure the button resets
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [error, success]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reason = params.get("reason");

    if (reason === "deactivated") {
      setDeactivationMessage("Your account has been suspended. Please contact support.");
    }
  }, [location.search]);

  return (
    <>
      <div className="h-[80vh] flex items-center justify-center px-4 bg-gray-100">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6 flex items-center justify-center gap-2">
            Sign In
          </h2>

          {error && (
            <div className="bg-red-100 text-red-600 text-sm px-3 py-2 rounded mb-3">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded mb-3">
              {success}
            </div>
          )}
          {deactivationMessage && (
            <div className="bg-yellow-100 text-yellow-800 p-3 rounded mb-4">
              {deactivationMessage}
            </div>
          )}
          {redirectMessage && (
            <div className="mb-4 p-3 rounded-md bg-yellow-100 border border-yellow-400 text-yellow-700 text-sm">
              {redirectMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Mail className="w-4 h-4 text-gray-500" /> Email Address
              </label>
              <input
                name="email"
                type="email"
                id="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Lock className="w-4 h-4 text-gray-500" /> Password
              </label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 focus:outline-none"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
                type="submit"
                disabled={loading}
                className={`w-full font-semibold py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200 
                  ${loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    Signing In...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" /> Sign In
                  </>
                )}
              </button>
          </form>

          <div className="mt-6">
            <div className="relative text-center mb-4">
              <span className="bg-white px-2 text-gray-400 text-sm">or</span>
              <div className="absolute top-1/2 left-0 right-0 border-t border-gray-300 -z-10"></div>
            </div>
            <button
              type="button"
              className="w-full border border-gray-300 hover:bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200"
              onClick={() => loginWithGoogle()}
            >
              <img src="/images/icons/google-logo.svg" alt="Google" className="w-5 h-5" />
              Sign In with Google
            </button>
          </div>

          <p className="mt-6 text-sm text-center text-gray-600">
            Don't have an account?{" "}
            <Link 
              to="/register"
              className="text-blue-600 hover:underline"
              state={{ from: location.state?.from || "/dashboard" }}
            >
              Register here
            </Link>
          </p>
        </div>
      </div>

      <AlertModal
        isOpen={alert.open}
        onClose={() => setAlert({ open: false, message: "", type: "info" })}
        message={alert.message}
        type={alert.type}
      />
    </>
  );
};

export default SignIn;
